import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useContext } from 'react';
import { useAppSelector } from 'hooks';
import { getAccessToken, getIdToken } from 'utils/auth';

import { CashierContext } from './CashierContext';

type PoolLimits = {
  dailyLimit: number;
  used: number;
  remaining: number;
  usagePercent: number;
};

type TodayDepositPayload = {
  totalDeposit: number;
  dailyLimit: number;
  remaining: number;
  minDeposit?: number;
  tier?: number;
};

type TodayWithdrawalPayload = {
  totalWithdrawal: number;
  dailyLimit: number;
  remaining: number;
  tier?: number;
};

type LimitState = {
  todayDeposit: TodayDepositPayload | null;
  todayWithdrawal: TodayWithdrawalPayload | null;
  currentTierNumber: number | null;
};

/**
 * Tier data from /api/risk/tiers. Matches the camelCased
 * `RiskService::retrieveRiskTiers` output, which mirrors the `risk_tiers` DB
 * columns plus the derived `userCount`. Any other field name is a phantom.
 *
 * `online_*` and `in_person_*` superseded the deprecated `global_*` columns
 * in the 2026_05_27 migration. The umbrella daily caps live on
 * `online_*_limit` for digital channels (card + crypto + p2p) and on
 * `in_person_*_limit` for cage. Per-channel caps still exist for
 * `cardDepositLimit` and `cryptoWithdrawalLimit`.
 */
export type TierData = {
  tier: number;
  handsPlayedMin: number;
  handsPlayedMax: number;
  cardDepositLimit: number;
  cryptoWithdrawalLimit: number;
  onlineDepositLimit: number;
  onlineWithdrawalLimit: number;
  inPersonDepositLimit: number;
  inPersonWithdrawalLimit: number;
  // The umbrella daily caps that actually exist in the committed risk_tiers
  // schema (add_global_limits migration; default 10000). The `in_person_*` /
  // `online_*` columns the fields above expect were never added to prod/staging,
  // so those come back undefined there — we fall back to these globals so the
  // In-Person pool shows the umbrella cap instead of $0.
  globalDepositLimit?: number;
  globalWithdrawalLimit?: number;
  requiredAccountAgeDays?: number;
  userCount?: number;
};

export type TierInfo = {
  current: TierData | null;
  next: TierData | null;
  all: TierData[];
};

export type CashierLimits = {
  tier: number;
  tierInfo: TierInfo;
  handsPlayed: number;
  idVerified: string;
  inPersonLimitsEnabled: boolean;
  withdrawalHandsPlayedMin: number;
  withdrawalHandsPlayedRemaining: number;
  withdrawalHandsRequirementMet: boolean;
  loading: boolean;
  error: string | null;
  deposit: {
    digital: PoolLimits;
    inperson: PoolLimits;
    methods: {
      creditCard: PoolLimits;
      crypto: PoolLimits;
      cage: PoolLimits;
    };
  };
  withdrawal: {
    digital: PoolLimits;
    inperson: PoolLimits;
    methods: {
      crypto: PoolLimits;
      p2p: PoolLimits;
      cage: PoolLimits;
    };
  };
  refresh: () => void;
};

function getBackofficeUrl(): string {
  return process.env.REACT_APP_BACKOFFICE_URL || `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`;
}

function createPoolLimit(dailyLimit: number, used: number): PoolLimits {
  const normalizedLimit = Math.max(0, Number(dailyLimit) || 0);
  const normalizedUsed = Math.max(0, Number(used) || 0);
  const remaining = normalizedLimit === 0 ? 0 : Math.max(0, normalizedLimit - normalizedUsed);
  const usagePercent = normalizedLimit > 0
    ? Math.min(100, (normalizedUsed / normalizedLimit) * 100)
    : 0;

  return {
    dailyLimit: normalizedLimit,
    used: normalizedUsed,
    remaining,
    usagePercent
  };
}

const FETCH_TIMEOUT_MS = 10_000;
const WITHDRAWAL_HANDS_PLAYED_MIN = 100;

/** Cross-browser AbortSignal.timeout — Node 18+ and modern browsers ship it
 * natively; older Safari needs the manual controller + setTimeout fallback. */
function timeoutSignal(ms: number): AbortSignal {
  if (typeof AbortSignal !== 'undefined' && typeof (AbortSignal as any).timeout === 'function') {
    return (AbortSignal as any).timeout(ms);
  }
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

async function fetchJson(url: string, options: RequestInit): Promise<any | null> {
  try {
    const res = await fetch(url, { ...options, signal: timeoutSignal(FETCH_TIMEOUT_MS) });
    if (!res.ok) {
      return null;
    }
    return await res.json();
  } catch {
    // Includes AbortError on timeout — fail-closed: caller treats null as a
    // missing payload and preserves cached state.
    return null;
  }
}

function getResponseData(payload: any): any {
  return payload?.data ?? payload ?? null;
}

export function useCashierLimits(): CashierLimits {
  const { isOpen } = useContext(CashierContext);
  const playerGUID = useAppSelector((state) => (state.user as any).playerGUID) || '';
  const storeHandsPlayed = useAppSelector((state) => (state.user as any).handsPlayed) || 0;
  const idVerified = useAppSelector((state) => (state.user as any).idVerified) || 'PENDING';

  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tiers, setTiers] = useState<TierData[]>([]);
  const [limitState, setLimitState] = useState<LimitState>({
    todayDeposit: null,
    todayWithdrawal: null,
    currentTierNumber: null
  });
  // Tracks whether we've ever loaded tier definitions successfully. Used to
  // decide between "first-load failure → show error" vs. "transient poll
  // failure → preserve cached state silently".
  const haveLoadedTiersRef = useRef(false);

  const fetchLimits = useCallback(async () => {
    if (!hasLoadedOnce) {
      setLoading(true);
    }
    setError(null);

    const accessToken = getAccessToken();
    const idToken = getIdToken();
    if (!accessToken || !idToken) {
      setLoading(false);
      return;
    }

    const base = getBackofficeUrl();
    const authHeaders = {
      Authorization: `Bearer ${idToken}`,
      'Content-Type': 'application/json',
      Accept: 'application/json'
    };
    // today/deposit and today/withdrawal derive playerGUID from the bearer token —
    // sending GUID or access token in the body is a duplication that leaks the token
    // into request-body capture (matches apps/payments which posts `{}`).

    try {
      const [
        tiersPayload,
        currentTierPayload,
        todayDepositPayload,
        todayWithdrawalPayload
      ] = await Promise.all([
        fetchJson(`${base}/api/risk/tiers`, {}),
        fetchJson(`${base}/api/risk/tier/current?playerGuid=${encodeURIComponent(playerGUID)}`, {
          method: 'GET',
          headers: authHeaders
        }),
        fetchJson(`${base}/api/players/today/deposit`, {
          method: 'POST',
          headers: authHeaders,
          body: '{}'
        }),
        fetchJson(`${base}/api/players/today/withdrawal`, {
          method: 'POST',
          headers: authHeaders,
          body: '{}'
        })
      ]);

      let nextTiers: TierData[] = [];
      if (Array.isArray(tiersPayload)) {
        nextTiers = tiersPayload.sort((a: TierData, b: TierData) => a.tier - b.tier);
      } else if (tiersPayload?.tiers && Array.isArray(tiersPayload.tiers)) {
        nextTiers = tiersPayload.tiers.sort((a: TierData, b: TierData) => a.tier - b.tier);
      }

      // Tier definitions are the source of truth for the CAPS and load
      // independently of the today/* usage calls — mirroring Unity, where a
      // failed daily-totals fetch only blanks the "used today" display and never
      // wipes the tier-derived limits (CryptoWithdrawView.cs:110-114 + the
      // separate GetRiskTiers call). The old code bundled all four atomically, so
      // a today/* 500 discarded the tiers that DID load and collapsed every cap
      // to 0 — disabling the Withdraw button for any amount.
      //
      // today/* is treated as USAGE only: when present it sets "used today";
      // when missing we keep the last-known usage (or default to 0), so the cap
      // still applies and the button stays usable.
      const haveTiers = nextTiers.length > 0 && currentTierPayload !== null;

      if (haveTiers) {
        setTiers(nextTiers);
        setLimitState((prev) => ({
          todayDeposit:
            todayDepositPayload !== null ? getResponseData(todayDepositPayload) : prev.todayDeposit,
          todayWithdrawal:
            todayWithdrawalPayload !== null
              ? getResponseData(todayWithdrawalPayload)
              : prev.todayWithdrawal,
          currentTierNumber: Number(currentTierPayload?.tier) || null
        }));
        setError(null);
        haveLoadedTiersRef.current = true;

        if (todayDepositPayload === null || todayWithdrawalPayload === null) {
          // Non-blocking: caps come from the tier; only the usage figure is stale.
          console.warn('[CashierLimits] today/* unavailable — using tier caps with last-known usage', {
            playerGUID,
            todayDepositOk: todayDepositPayload !== null,
            todayWithdrawalOk: todayWithdrawalPayload !== null
          });
        }
      } else if (haveLoadedTiersRef.current) {
        // Transient poll failure after a prior success: warn so support can
        // triage but preserve the cached pools.
        console.warn(
          '[CashierLimits] Partial fetch — preserving cached state',
          {
            playerGUID,
            tiersOk: nextTiers.length > 0,
            currentTierOk: currentTierPayload !== null,
            todayDepositOk: todayDepositPayload !== null,
            todayWithdrawalOk: todayWithdrawalPayload !== null
          }
        );
      } else {
        setError('Could not load cashier limits.');
      }
    } catch (e) {
      console.warn('[CashierLimits] Failed to fetch limits:', e);
      if (!haveLoadedTiersRef.current) {
        setError('Could not load cashier limits.');
      }
    } finally {
      setHasLoadedOnce(true);
      setLoading(false);
    }
  }, [hasLoadedOnce, playerGUID]);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    if (!isOpen) return;

    const intervalId = window.setInterval(() => {
      fetchLimits();
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isOpen, fetchLimits]);

  return useMemo(() => {
    const todayDeposit = limitState.todayDeposit || ({} as TodayDepositPayload);
    const todayWithdrawal = limitState.todayWithdrawal || ({} as TodayWithdrawalPayload);

    const handsPlayed = Number(storeHandsPlayed) || 0;

    // Current tier is derived from hands played against the tier definitions —
    // the highest tier whose handsPlayedMin the player has reached. This mirrors
    // the engine/Unity ("You've played N hands!" → Tier X) and the backend's
    // `hands_played_min <= handsPlayed` rule, and keeps the badge consistent
    // with the progress bar. The server tier number (api/risk/tier/current) is
    // only a fallback for when we don't yet have a hands count or tier defs —
    // previously it was the sole source and rendered Tier 1 whenever that call
    // was unavailable (NaN coalescing → tiers[0]).
    const serverTierNumber =
      limitState.currentTierNumber ||
      Number(todayDeposit.tier) ||
      Number(todayWithdrawal.tier) ||
      null;
    const derivedTier =
      tiers.length > 0
        ? [...tiers].reverse().find((t) => handsPlayed >= t.handsPlayedMin) ?? tiers[0]
        : null;
    const currentTier =
      derivedTier ||
      (serverTierNumber ? tiers.find((t) => t.tier === serverTierNumber) : null) ||
      tiers[0] ||
      null;
    const tierNumber = currentTier?.tier ?? serverTierNumber ?? 1;
    const nextTier = currentTier
      ? tiers.find((t) => t.tier === currentTier.tier + 1) || null
      : null;
    const tierInfo: TierInfo = {
      current: currentTier,
      next: nextTier,
      all: tiers
    };

    const totalDeposit = Number(todayDeposit.totalDeposit) || 0;
    const totalWithdrawal = Number(todayWithdrawal.totalWithdrawal) || 0;

    // Per-method caps come straight from the admin-configured tier columns —
    // NOT the grouped online_*/in_person_* umbrella caps. That pooled behavior
    // is not released yet, so the Cashier follows the current Unity limit
    // behavior: each method uses its own configured limit.
    //   creditCard → cardDepositLimit         crypto withdraw → cryptoWithdrawalLimit
    //   cage        → inPerson* (fixed)         player transfer → no configured cap
    // today/*.dailyLimit is only a tier-derived fallback so a tier that leaves a
    // per-channel column unset doesn't hard-lock the method to $0.
    const cardDepositLimit =
      Number(currentTier?.cardDepositLimit) || Number(todayDeposit.dailyLimit) || 0;
    // Cage's in-person cap: prefer the dedicated in_person column, but that
    // column isn't in the committed risk_tiers schema (only local dev DBs have
    // it), so prod/staging fall back to the global umbrella cap — otherwise
    // In-Person always reads $0 in those environments. No fallback to a deposit
    // cap (a card limit is not an in-person limit).
    const inPersonDepositLimit =
      Number(currentTier?.inPersonDepositLimit) || Number(currentTier?.globalDepositLimit) || 0;
    // Crypto deposit has no released per-channel cap; mirror the card cap rather
    // than a shared online pool.
    const cryptoDepositLimit = cardDepositLimit;

    const cryptoWithdrawalLimit =
      Number(currentTier?.cryptoWithdrawalLimit) || Number(todayWithdrawal.dailyLimit) || 0;
    // Same fallback as in-person deposit: dedicated column if present, else the
    // committed global umbrella cap (prod/staging lack the in_person column).
    const inPersonWithdrawalLimit =
      Number(currentTier?.inPersonWithdrawalLimit) || Number(currentTier?.globalWithdrawalLimit) || 0;
    // Player transfer (p2p) has no configured tier column → no tiered cap. The
    // Withdraw view treats a 0 p2p cap as "no limit" (balance is the ceiling).
    const p2pWithdrawalLimit = 0;

    // Previews the backoffice's fixed 100-hand rule, which rejects cage
    // (CTransactions), crypto (CoinbaseSendFundingService) and player transfer
    // (PlayerChipTransferService) below it. The server stays authoritative.
    const withdrawalHandsPlayedMin = WITHDRAWAL_HANDS_PLAYED_MIN;
    const withdrawalHandsPlayedRemaining = Math.max(0, withdrawalHandsPlayedMin - handsPlayed);
    const withdrawalHandsRequirementMet = withdrawalHandsPlayedRemaining <= 0;

    return {
      tier: tierNumber,
      tierInfo,
      handsPlayed,
      idVerified,
      inPersonLimitsEnabled: false,
      withdrawalHandsPlayedMin,
      withdrawalHandsPlayedRemaining,
      withdrawalHandsRequirementMet,
      loading,
      error,
      deposit: {
        // The "Digital" pool represents the credit-card deposit path (its tier
        // limit); "In-Person" is the cage's own fixed limit.
        digital: createPoolLimit(cardDepositLimit, totalDeposit),
        inperson: createPoolLimit(inPersonDepositLimit, 0),
        methods: {
          creditCard: createPoolLimit(cardDepositLimit, totalDeposit),
          crypto: createPoolLimit(cryptoDepositLimit, 0),
          cage: createPoolLimit(inPersonDepositLimit, 0)
        }
      },
      withdrawal: {
        // "Digital" withdrawal represents crypto (its tier limit); p2p carries
        // no tiered cap. "In-Person" is the cage's own fixed limit.
        digital: createPoolLimit(cryptoWithdrawalLimit, totalWithdrawal),
        inperson: createPoolLimit(inPersonWithdrawalLimit, 0),
        methods: {
          crypto: createPoolLimit(cryptoWithdrawalLimit, totalWithdrawal),
          p2p: createPoolLimit(p2pWithdrawalLimit, totalWithdrawal),
          cage: createPoolLimit(inPersonWithdrawalLimit, 0)
        }
      },
      refresh: fetchLimits
    };
  }, [storeHandsPlayed, idVerified, tiers, limitState, loading, error, fetchLimits]);
}
